import React, { useEffect } from "react";
import {
  FileText,
  Users,
  PieChart,
  RefreshCcw,
  Shield,
  Layout,
} from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VisualShowcase } from "../ui/VisualShowcase/VisualShowcase";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { BulletList, BulletItem } from "../ui/Bullets/Bullets";
import { VideoEmbed } from "../ui/VideoEmbed/VideoEmbed";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import images from "../../../constants/images";

import agileProjectManagement from "../../../assets/videos/agile-project-management.mp4";

import "./Agile.scss";

const Agile = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const BulletListRight = (
    <BulletList>
      <BulletItem>
        Framing Agile not merely as a set of ceremonies, but as a mindset of
        iterative learning
      </BulletItem>
      <BulletItem>
        Embedding feedback loops across design, data, and delivery
      </BulletItem>
      <BulletItem>
        Using visual planning tools (e.g., Kanban or backlog boards) to create
        transparency and prioritisation
      </BulletItem>
      <BulletItem>
        Focusing on outcomes over outputs to inform decision‑making and risk
        management
      </BulletItem>
    </BulletList>
  );

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Understanding Agile Project Management"
        subtitle="How strategic agility enables better product outcomes by blending design, data, and technology."
        category="CONCEPT"
        role="Principal Software Engineer"
        year="2018 - 2019"
        platform="Web / Cross-disciplinary digital projects"
        technologies={[
          "Jira, Confluence, Figma, Miro, React, Node.js, API integration",
        ]}
        client="INFO Business Innovation Partner"
      />
      <VisualShowcase
        imageUrl={images.infoOffice}
        imageAlt="Info Office"
        layout="full"
      />
      <ProjectOverview
        challengeContent={[
          "Adapting Agile for strategic, real‑world delivery",
          "Traditional project management approaches often assume predictable requirements and fixed scopes — assumptions that break down in complex digital transformations.",
          "INFO’s clients navigated shifting priorities, evolving markets, and multidisciplinary demands for quality, speed, and alignment across stakeholders.",
          "The challenge was not just applying Agile methods, but adapting them strategically so they could guide planning, execution, and learning in real, ambiguous contexts.",
        ]}
        solutionContent={[
          "Tailoring Agile as a strategic, cross‑disciplinary process",
          "We articulated how Agile principles could unite product strategy, design thinking, data insight, and technology execution into a coherent delivery approach. This involved:",
        ]}
        extraSolutionContent={[
          "Rather than adopting a one‑size‑fits‑all flavour of Agile, we shaped a context‑aware approach that balanced flexibility, predictability, and strategic alignment.",
        ]}
        bulletsRight={BulletListRight}
        metrics={[
          { value: "Faster", label: "Iteration feedback cycles" },
          {
            value: "More aligned",
            label: "Cross‑discipline collaboration outcomes",
          },
          {
            value: "Increased clarity",
            label: "Work prioritisation and transparency",
          },
          { value: "Improved quality", label: "Early validation of concepts" },
        ]}
      />
      <VideoEmbed
        src={agileProjectManagement}
        poster={images.agileImg}
        title="Agile Product Development in Practice"
        description="A walkthrough of how Agile principles were interpreted and applied at INFO to guide project delivery and strategic decision‑making. This video illustrates the mindset, adaptations, and practical steps that enabled Agile to deliver real value"
        controls={true}
      />
      <KeyFeatures
        title="Core Elements of Agile Delivery at INFO"
        description="Features and practices designed to balance strategy, design, data, and technology while enabling iterative learning."
        features={[
          {
            icon: FileText, // placeholder, could use a generic document or checklist icon
            title: "Iterative Planning & Prioritisation",
            description:
              "Organised work into short cycles with clear prioritisation to respond rapidly to shifting requirements and emerging insights.",
          },
          {
            icon: Users, // placeholder for team collaboration icon
            title: "Cross-Disciplinary Collaboration",
            description:
              "Embedded design, data, and engineering teams into cohesive workflows to ensure alignment and shared understanding.",
          },
          {
            icon: PieChart, // placeholder for measurement/analytics icon
            title: "Outcome-Focused Metrics",
            description:
              "Tracked feedback loops, progress, and key performance indicators to ensure decisions were informed by evidence, not assumptions.",
          },
          {
            icon: Layout, // placeholder for tools/tech icon
            title: "Agile Tooling & Visual Management",
            description:
              "Used Kanban boards, backlogs, and visual workflows to maintain transparency, clarity, and accountability across teams.",
          },
          {
            icon: RefreshCcw, // placeholder for iterative improvement icon
            title: "Continuous Feedback & Learning",
            description:
              "Held regular retrospectives and reviews to refine both processes and solutions, creating a culture of continuous improvement.",
          },
          {
            icon: Shield, // placeholder for risk/compliance icon
            title: "Strategic Risk Management",
            description:
              "Applied Agile methods to anticipate blockers, identify risks, and make informed trade-offs without compromising delivery quality.",
          },
        ]}
      />
      <ProcessSection
        title="Agile in Action at INFO"
        description="A people‑centric, iterative approach that unites design, data, and technology for resilient product delivery."
        phases={[
          {
            number: "01",
            title: "Frame & Align",
            description:
              "Clarified strategic outcomes, stakeholder expectations, and the core problem space to guide iterative delivery planning.",
            deliverables: [
              "Value definition workshops",
              "Initial backlog creation",
              "Stakeholder alignment sessions",
            ],
          },
          {
            number: "02",
            title: "Iterate & Validate",
            description:
              "Delivered in short cycles with frequent feedback from users and stakeholders to inform next steps and priorities.",
            deliverables: [
              "Sprint/iteration demos",
              "Prototypes and user tests",
              "Data‑informed adjustments",
            ],
          },
          {
            number: "03",
            title: "Reflect & Improve",
            description:
              "Held retrospectives and reviews to identify bottlenecks, technical risks, and opportunities for process refinement.",
            deliverables: [
              "Retrospective outputs",
              "Process improvement actions",
              "Updated prioritisation",
            ],
          },
          {
            number: "04",
            title: "Scale & Embed",
            description:
              "Embedded learnings and refined methods into broader organisational practices to elevate delivery effectiveness.",
            deliverables: [
              "Best practice playbooks",
              "Agile coaching insights",
              "Wider team enablement",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactDescription="INFO’s tailored Agile approach improved team alignment, accelerated feedback cycles, and supported better strategic decisions. By treating Agile as a mindset rather than a checklist, teams could continuously learn, adapt, and deliver higher-value outcomes across design, data, and technology."
        impactLabel="Project Impact"
        impactTitle="Strategic agility driving measurable outcomes"
        nextProject={{
          title: "AI Chatbot Assistant",
          link: "/project/chapter-works",
        }}
        conclusion={{
          label: "Design / Engineering Reflection",
          title: "Agile as strategic craft, not checklist",
          description:
            "Applying Agile at INFO reinforced that process is not the goal — mindset is. Success comes from empowering teams with clarity, transparency, and iterative learning. This project demonstrated how Agile can be adapted strategically to guide execution, foster alignment, and support insight-driven decisions across design, data, and technology.",
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export { Agile };

/*
 Might put a description on each page (not there yet), just to explain who the company is.

 INFO combines design, data, and technology on a strategic level to create the right digital solutions that empower organisations. In this piece, I explore how we applied Agile principles at INFO to improve delivery and collaboration.
*/
